import '@backstage/cli/asset-types';
import ReactDOM from 'react-dom/client';
import App from './App';
import '@backstage/ui/css/styles.css';
import { AuthProvider } from './auth/AuthContext';
import { AuthGate } from './auth/AuthGate';
import { authApi, BACKSTAGE_TOKEN_KEY } from './auth/authApi';
import './auth/role-visibility.css';
import './styles/dialog.css';

const CATALOG_IMPORT_TITLE = 'Register an existing component';
const API_ADD_BUTTON_PATTERN = /add\s+new\s+api/i;
const API_LOCAL_UPLOAD_BUTTON_LABEL = 'LOCAL UPLOAD';
const LOCAL_UPLOAD_TOAST_ID = 'local-api-upload-toast';

const getApiDocsActionCandidates = () =>
  Array.from(
    document.querySelectorAll('button, a[href], [role="button"]'),
  ) as HTMLElement[];

const getBackendOrigin = () =>
  window.location.port === '3000'
    ? `${window.location.protocol}//${window.location.hostname}:7007`
    : window.location.origin;

const showLocalUploadMessage = (message: string, isError = false) => {
  let toast = document.getElementById(LOCAL_UPLOAD_TOAST_ID) as HTMLDivElement | null;

  if (!toast) {
    toast = document.createElement('div');
    toast.id = LOCAL_UPLOAD_TOAST_ID;
    toast.style.position = 'fixed';
    toast.style.right = '16px';
    toast.style.bottom = '16px';
    toast.style.zIndex = '9999';
    toast.style.maxWidth = '480px';
    toast.style.padding = '12px 14px';
    toast.style.borderRadius = '8px';
    toast.style.color = '#ffffff';
    toast.style.fontSize = '13px';
    toast.style.whiteSpace = 'pre-wrap';
    toast.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.22)';
    document.body.appendChild(toast);
  }

  toast.style.background = isError ? '#b00020' : '#1f6f43';
  toast.textContent = message;

  window.setTimeout(() => {
    toast?.remove();
  }, 7000);
};

const dryRunCatalogLocation = async (
  backstageToken: string,
  target: string,
): Promise<
  | { ok: true; expectedApiNames: string[] }
  | { ok: false; error: string }
> => {
  const response = await window.fetch(`${getBackendOrigin()}/api/catalog/locations?dryRun=true`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${backstageToken}`,
    },
    body: JSON.stringify({
      type: 'url',
      target,
    }),
  });

  if (response.ok) {
    const body = (await response.json()) as {
      entities?: Array<{ kind?: string; metadata?: { name?: string } }>;
    };
    const expectedApiNames =
      body.entities
        ?.filter(entity => entity.kind?.toLowerCase() === 'api')
        .map(entity => entity.metadata?.name)
        .filter((name): name is string => Boolean(name)) ?? [];
    return { ok: true, expectedApiNames };
  }

  const body = (await response.json().catch(() => null)) as
    | { error?: { message?: string } | string }
    | null;
  const errorMessage =
    typeof body?.error === 'string' ? body.error : body?.error?.message ?? `HTTP ${response.status}`;
  return { ok: false, error: errorMessage };
};

const waitForApisByName = async (
  backstageToken: string,
  expectedApiNames: string[],
): Promise<number> => {
  if (expectedApiNames.length === 0) {
    return 0;
  }

  const uniqueNames = Array.from(new Set(expectedApiNames));
  const discovered = new Set<string>();

  for (let attempt = 0; attempt < 15; attempt += 1) {
    for (const apiName of uniqueNames) {
      if (discovered.has(apiName)) {
        continue;
      }

      const response = await window.fetch(
        `${getBackendOrigin()}/api/catalog/entities/by-query?filter=kind=api&filter=metadata.name=${encodeURIComponent(apiName)}&limit=1`,
        {
          headers: {
            Authorization: `Bearer ${backstageToken}`,
          },
        },
      );

      if (!response.ok) {
        continue;
      }

      const body = (await response.json()) as { items?: unknown[] };
      if ((body.items?.length ?? 0) > 0) {
        discovered.add(apiName);
      }
    }

    if (discovered.size === uniqueNames.length) {
      break;
    }

    await new Promise(resolve => {
      window.setTimeout(resolve, 1000);
    });
  }

  return discovered.size;
};

const removeCatalogImportSection = () => {
  if (!window.location.pathname.startsWith('/catalog-import')) {
    return;
  }

  const headings = Array.from(
    document.querySelectorAll('h1, h2, h3, h4, h5, h6, span, p, div'),
  );

  for (const node of headings) {
    const text = node.textContent?.trim();
    if (text === CATALOG_IMPORT_TITLE) {
      const panel =
        node.closest('[class*="MuiPaper-root"]') ??
        node.closest('[class*="MuiCard-root"]') ??
        node.closest('section') ??
        node.closest('div');
      panel?.remove();
    }
  }
};

const enforceApiWriteAccess = () => {
  const isAdmin = document.body.classList.contains('admin-user');
  const isCatalogImport = window.location.pathname.startsWith('/catalog-import');

  if (!isAdmin && isCatalogImport) {
    window.history.replaceState({}, '', '/api-docs');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
};

const removeApiAddButtonForNonAdmin = () => {
  const isAdmin = document.body.classList.contains('admin-user');
  if (isAdmin || !window.location.pathname.startsWith('/api-docs')) {
    return;
  }

  const candidates = getApiDocsActionCandidates();

  for (const node of candidates) {
    const text = node.textContent?.replace(/\s+/g, ' ').trim();
    const isAddButtonLabel = Boolean(text && API_ADD_BUTTON_PATTERN.test(text));
    const href = node.getAttribute('href') ?? '';
    const isCatalogImportLink = href.includes('/catalog-import');

    if (isAddButtonLabel || isCatalogImportLink) {
      const button = node.closest('button') ?? node.closest('a[href]') ?? node;
      button.remove();
    }
  }
};

const ensureLocalApiUploadButton = () => {
  const isApiDocsRoute = window.location.pathname.startsWith('/api-docs');

  if (!isApiDocsRoute) {
    document.getElementById('local-api-upload-button')?.remove();
    document.getElementById('local-api-upload-input')?.remove();
    return;
  }

  const candidates = getApiDocsActionCandidates();

  let addButton: HTMLElement | null = null;
  for (const node of candidates) {
    const text = node.textContent?.replace(/\s+/g, ' ').trim();
    const isAddButtonLabel = Boolean(text && API_ADD_BUTTON_PATTERN.test(text));
    const href = node.getAttribute('href') ?? '';
    const isCatalogImportLink = href.includes('/catalog-import');

    if (isAddButtonLabel || isCatalogImportLink) {
      addButton = (node.closest('button') ?? node.closest('a[href]') ?? node) as HTMLElement;
      break;
    }
  }

  if (!addButton) {
    document.getElementById('local-api-upload-button')?.remove();
    document.getElementById('local-api-upload-input')?.remove();
    return;
  }

  let uploadInput = document.getElementById('local-api-upload-input') as
    | HTMLInputElement
    | null;
  if (!uploadInput) {
    uploadInput = document.createElement('input');
    uploadInput.id = 'local-api-upload-input';
    uploadInput.type = 'file';
    uploadInput.accept = '.zip,application/zip';
    uploadInput.style.display = 'none';
    uploadInput.addEventListener('change', async () => {
      if (!uploadInput) {
        return;
      }

      const file = uploadInput.files?.[0];
      if (!file) {
        return;
      }

      const isZip = file.name.toLowerCase().endsWith('.zip');
      if (!isZip) {
        showLocalUploadMessage('Please select a ZIP file for API bundle upload.', true);
        uploadInput.value = '';
        return;
      }

      const localToken = authApi.getToken();
      if (!localToken) {
        showLocalUploadMessage('Please sign in before uploading an API bundle.', true);
        uploadInput.value = '';
        return;
      }

      const backstageToken = localStorage.getItem(BACKSTAGE_TOKEN_KEY);
      if (!backstageToken) {
        showLocalUploadMessage(
          'Missing Backstage session token. Please sign out and sign in again.',
          true,
        );
        uploadInput.value = '';
        return;
      }

      try {
        const { catalogUrls } = await authApi.uploadApiBundle(localToken, file);
        let registeredCount = 0;
        const failedTargets: string[] = [];
        let alreadyRegisteredCount = 0;
        const expectedApiNames: string[] = [];

        for (const catalogUrl of catalogUrls) {
          const dryRunResult = await dryRunCatalogLocation(backstageToken, catalogUrl);
          if (!dryRunResult.ok) {
            failedTargets.push(`${catalogUrl} (${dryRunResult.error})`);
            continue;
          }
          expectedApiNames.push(...dryRunResult.expectedApiNames);

          const response = await window.fetch(
            `${getBackendOrigin()}/api/catalog/locations?onConflict=refresh`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${backstageToken}`,
              },
              body: JSON.stringify({
                type: 'url',
                target: catalogUrl,
              }),
            },
          );

          const responseBody = (await response.json().catch(() => null)) as
            | {
                exists?: boolean;
                error?: { message?: string } | string;
              }
            | null;

          if (response.ok) {
            registeredCount += 1;
            if (responseBody?.exists) {
              alreadyRegisteredCount += 1;
            }
            continue;
          }

          const errorBody = responseBody;
          const errorMessage =
            typeof errorBody?.error === 'string'
              ? errorBody.error
              : errorBody?.error?.message ?? `HTTP ${response.status}`;
          failedTargets.push(`${catalogUrl} (${errorMessage})`);
        }

        const expectedApisDiscovered = await waitForApisByName(backstageToken, expectedApiNames);
        const uniqueExpectedApiCount = Array.from(new Set(expectedApiNames)).length;

        if (failedTargets.length > 0) {
          showLocalUploadMessage(
            `API bundle processed. Registered ${registeredCount}/${catalogUrls.length} location(s).\n\nFailures:\n${failedTargets.join('\n')}`,
            true,
          );
        } else if (expectedApisDiscovered === 0) {
          showLocalUploadMessage(
            `Location(s) registered but no new APIs appeared yet.\nAlready registered: ${alreadyRegisteredCount}/${catalogUrls.length}.\nExpected APIs discovered: ${expectedApisDiscovered}/${uniqueExpectedApiCount}.`,
            true,
          );
        } else {
          showLocalUploadMessage(
            `API bundle uploaded successfully. Registered ${registeredCount} location(s) and discovered ${expectedApisDiscovered}/${uniqueExpectedApiCount} expected API(s).`,
          );
        }
      } catch (error) {
        showLocalUploadMessage((error as Error).message || 'Failed to upload API bundle.', true);
      } finally {
        uploadInput.value = '';
      }
    });
    document.body.appendChild(uploadInput);
  }

  if (document.getElementById('local-api-upload-button')) {
    return;
  }

  const uploadButton = document.createElement('button');
  uploadButton.id = 'local-api-upload-button';
  uploadButton.type = 'button';
  uploadButton.className = addButton.className;
  uploadButton.textContent = API_LOCAL_UPLOAD_BUTTON_LABEL;
  uploadButton.style.marginLeft = '8px';
  uploadButton.addEventListener('click', event => {
    event.preventDefault();
    uploadInput?.click();
  });

  addButton.insertAdjacentElement('afterend', uploadButton);
};

const updateCatalogImportClass = () => {
  const isCatalogImport = window.location.pathname.startsWith('/catalog-import');
  const isSearchRoute = window.location.pathname.startsWith('/search');
  const isApiDocsRoute = window.location.pathname.startsWith('/api-docs');
  document.body.classList.toggle('catalog-import-route', isCatalogImport);
  document.body.classList.toggle('search-route', isSearchRoute);
  document.body.classList.toggle('api-docs-route', isApiDocsRoute);
  removeCatalogImportSection();
  enforceApiWriteAccess();
  removeApiAddButtonForNonAdmin();
  ensureLocalApiUploadButton();
};

const originalPushState = window.history.pushState.bind(window.history);
window.history.pushState = function pushState(...args) {
  const result = originalPushState(...args);
  updateCatalogImportClass();
  return result;
};

const originalReplaceState = window.history.replaceState.bind(window.history);
window.history.replaceState = function replaceState(...args) {
  const result = originalReplaceState(...args);
  updateCatalogImportClass();
  return result;
};

window.addEventListener('popstate', updateCatalogImportClass);
updateCatalogImportClass();

// TechDocs reader pages (/docs/<namespace>/<kind>/<name>) render correctly only
// on a full page load. When reached via in-app SPA navigation the mkdocs theme
// stylesheets are not attached to the reader's shadow DOM, so the page renders
// unstyled (and previously hung on the loading bar). Forcing a real navigation
// for those links makes the docs load through the working full-load path.
// The docs index (/docs) and links inside the rendered docs (shadow DOM, whose
// clicks are retargeted to the host) are intentionally left untouched.
const TECHDOCS_READER_PATH = /^\/docs\/[^/]+\/[^/]+\/[^/]+/;
document.addEventListener(
  'click',
  event => {
    const mouse = event as MouseEvent;
    if (
      mouse.defaultPrevented ||
      mouse.button !== 0 ||
      mouse.metaKey ||
      mouse.ctrlKey ||
      mouse.shiftKey ||
      mouse.altKey
    ) {
      return;
    }
    const anchor = (mouse.target as HTMLElement | null)?.closest?.('a[href]') as
      | HTMLAnchorElement
      | null;
    if (!anchor || anchor.target === '_blank') {
      return;
    }
    let url: URL;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch {
      return;
    }
    if (
      url.origin === window.location.origin &&
      TECHDOCS_READER_PATH.test(url.pathname) &&
      url.pathname !== window.location.pathname
    ) {
      event.preventDefault();
      window.location.assign(url.href);
    }
  },
  true,
);

const catalogImportObserver = new MutationObserver(() => {
  removeCatalogImportSection();
  removeApiAddButtonForNonAdmin();
  ensureLocalApiUploadButton();
});

catalogImportObserver.observe(document.body, {
  childList: true,
  subtree: true,
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <AuthProvider>
    <AuthGate>{App.createRoot()}</AuthGate>
  </AuthProvider>,
);
